/**
 * @module Cursor offset mapping for the Tiptap rich text composer.
 *
 * Ported from t3code's composer rich-text layer (ticket 3526, upstream pin
 * e3e7cc3f, MIT license retained in LICENSE). Converts offsets between the
 * flat document, the collapsed draft-store cursor, prompt text, and
 * ProseMirror positions using the run map the serializer produces.
 */

import type { RichDocMap, RichRun } from "./composer-rich-text-serialize";

function lastRunEnd(map: RichDocMap, space: "collapsed" | "md"): number {
  const last = map.runs[map.runs.length - 1];
  if (!last) return 0;
  return space === "collapsed"
    ? last.collapsedStart + last.collapsedLen
    : last.mdStart + last.mdLen;
}

type SpaceCoordinate = "collapsed" | "md";

function flatToSpace(
  map: RichDocMap,
  flatOffset: number,
  space: SpaceCoordinate,
  startOf: (run: RichRun) => number,
): number {
  const bounded = Math.max(0, Math.min(flatOffset, map.docLength));
  for (const run of map.runs) {
    if (bounded < run.flatStart + run.docLen) {
      if (run.kind === "text" || run.kind === "token") {
        return startOf(run) + run.openLen + (bounded - run.flatStart);
      }
      return startOf(run) + (bounded - run.flatStart);
    }
  }
  return lastRunEnd(map, space);
}

export function flatToCollapsed(map: RichDocMap, flatOffset: number): number {
  return flatToSpace(map, flatOffset, "collapsed", (run) => run.collapsedStart);
}

export function flatToMarkdown(map: RichDocMap, flatOffset: number): number {
  return flatToSpace(map, flatOffset, "md", (run) => run.mdStart);
}

// fallow-ignore-next-line complexity -- ported upstream t3code algorithm (ticket 3526); behavior pinned by the ported spec
export function collapsedToFlat(map: RichDocMap, collapsedOffset: number): number {
  for (const run of map.runs) {
    if (collapsedOffset < run.collapsedStart + run.collapsedLen) {
      // Checkbox prefixes and style markers are shown, never edited: every
      // offset inside them clamps to the adjacent document position.
      if (run.kind === "prefix") return run.flatStart;
      if (run.kind === "text" || run.kind === "token") {
        const within = collapsedOffset - run.collapsedStart;
        // Marker characters clamp to the styled edge: shown, never edited.
        if (within <= run.openLen) return run.flatStart;
        if (within >= run.openLen + run.docLen) return run.flatStart + run.docLen;
        return run.flatStart + (within - run.openLen);
      }
      return run.flatStart + (collapsedOffset - run.collapsedStart);
    }
  }
  return map.docLength;
}

export function flatToPm(map: RichDocMap, flatOffset: number): number {
  const bounded = Math.max(0, Math.min(flatOffset, map.docLength));
  for (const run of map.runs) {
    if (bounded < run.flatStart + run.docLen) {
      return run.pmPos + (bounded - run.flatStart);
    }
  }
  const last = map.runs[map.runs.length - 1];
  if (!last) return 1;
  return last.pmPos + last.docLen;
}

export function pmToFlat(map: RichDocMap, pmPos: number): number {
  for (const run of map.runs) {
    if (pmPos >= run.pmPos && pmPos <= run.pmPos + run.docLen) {
      // A position on a chip's trailing edge belongs after the chip.
      if (run.kind === "token" && pmPos === run.pmPos + run.docLen) {
        return run.flatStart + run.docLen;
      }
      return run.flatStart + Math.min(pmPos - run.pmPos, run.docLen);
    }
  }
  // A paragraph boundary position belongs to the newline between paragraphs.
  let best = 0;
  for (const run of map.runs) {
    if (run.pmPos <= pmPos) best = run.flatStart + run.docLen;
  }
  return Math.max(0, Math.min(best, map.docLength));
}
